import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { get as _get, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

import {
  BillAccountActions,
  BillAccountsSelectors,
  BillingNotificationsState,
  BillingUtilService
} from '@amfam/billing/billaccount/data-access';
import { EsignatureSelectors, PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import { GoPaperLessActions, GoPaperLessSelectors } from '@amfam/policy/go-paperless/data-access';
import { PolicyAnalytics } from '@amfam/policy/models';
import { ButtonAnalyticParams, PageAnalyticParams } from '@amfam/shared/analytics';
import {
  BillAccountData,
  DeliveryPreferences,
  NewBillingPreference,
  UpdatePreferencesRequest
} from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';

@Component({
  selector: 'ds-post-enrollment-gopaperless',
  templateUrl: './post-enrollment-gopaperless.component.html',
  standalone: false
})
export class PostEnrollmentGopaperlessComponent implements OnInit, OnDestroy, AfterViewInit {
  public userEmailAddress$: Observable<string>;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  private newBillingPreferences: NewBillingPreference[] = [];
  public showModal = false;
  public showPaperLess = false;
  public isLoading: Observable<boolean>;
  private dispatched: boolean;

  constructor(
    private dockingService: DockingBarService,
    private store: Store<fromRoot.RootState>,
    private spinner: LoadingSpinnerService,
    private utilService: UtilService,
    private route: ActivatedRoute,
    private billingUtilService: BillingUtilService,
    private policyUtilService: PolicyUtilService
  ) {}

  ngOnInit() {
    this.spinner.start({
      blockActions: true
    });

    this.dockingService.registerHeading('Go Paperless');
    this.userEmailAddress$ = this.store.pipe(select(userQuery.selectEmailAddress));

    // AS: Set the state of the spinner based on the loading of the data.
    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingPreferences),
      this.userEmailAddress$,
      (billAccountLoading, preferenceLoading, emailAddress) => ({
        billAccountLoading,
        preferenceLoading,
        emailAddress
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.billAccountLoading || state.preferenceLoading || !state.emailAddress) {
          this.spinner.start();
        } else {
          this.spinner.stop();
        }
      });

    // AS: If there is an error we need to navigate to the overview screen, else if the data
    // is still loading keep the spinner on.
    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingRegistration),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsWithUpdatePreferenceFailure),
      (isUpdating, paperLessLoading, billAccountsWithPrefFailure) => {
        return { isUpdating, paperLessLoading, billAccountsWithPrefFailure };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (
          !state.isUpdating &&
          !state.paperLessLoading &&
          _get(state, 'billAccountsWithPrefFailure.length') > 0
        ) {
          this.spinner.stop();
          this.navigateToOverview(false, true);
        } else if (state.isUpdating || state.paperLessLoading) {
          this.spinner.start();
        }
      });

    /**
     * @description: This gets called after the docusign returns us to this page, check for the param
     * and if it has the SigningComplete then redirect the user to the overview screen.
     */
    this.route.queryParams
      .pipe(
        filter(params => params && !!params['event']),
        takeUntil(this.stop$)
      )
      .subscribe(params => {
        if (params['event'] === 'OnSigningComplete') {
          this.enrollAfterDocuSign();
        } else {
          this.navigateToOverview(false, true);
        }
      });
  }

  /**
   * @description: Call the analytics after the page has been initialized and ready to view/interact.
   * This prevents the page from being blocked by the non essential analytics call.
   */
  ngAfterViewInit() {
    this.dispatchAnalyticsAction(PolicyAnalytics.pageGoPaperlessNewCustomerStart, null);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  goPaperLess(): void {
    this.spinner.start({
      blockActions: true
    });

    // AS: Dispatch an action to register for go paperless for policy document for the non georgia
    // customers.
    combineLatest(
      this.store.select(EsignatureSelectors.selectESignatureRequired),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      (isESignatureRequired, policyPreference) => ({
        isESignatureRequired,
        policyPreference
      })
    )
      .pipe(take(1))
      .subscribe(state => {
        // Set policy preferences for non georgia customers.
        if (!state.isESignatureRequired) {
          const policyPreference = state.policyPreference
            ? DeliveryPreferences.EMAIL
            : DeliveryPreferences.PAPER;
          if (policyPreference === DeliveryPreferences.PAPER) {
            this.store.dispatch(GoPaperLessActions.EnrollGoPaperless());
          }
        }
      });

    // AS: Get the bill accounts from the store and call the function to build the new billing preference.
    this.store
      .pipe(select(BillAccountsSelectors.selectBillAccounts), take(1))
      .subscribe(billAccounts => {
        this.newBillingPreferences =
          this.billingUtilService.setBillAccountsToPaperless(billAccounts);
      });

    const correlationId = this.utilService.generateId();

    // AS: Get all the new billing preference and call the action to update the new preference one by one.
    if (this.newBillingPreferences && this.newBillingPreferences.length > 0) {
      this.newBillingPreferences.forEach(newPreference => {
        this.store
          .select(
            BillAccountsSelectors.selectBillAccountDataToUpdatePreferences(
              newPreference.billAccountNumber
            )
          )
          .pipe(take(1))
          .subscribe((billAccountData: BillAccountData) => {
            const preferencesObj: UpdatePreferencesRequest =
              this.billingUtilService.buildPreferencePayload(
                newPreference,
                billAccountData,
                correlationId
              );
            this.store.dispatch(
              BillAccountActions.billAccountUpdateDeliveryPreference(preferencesObj)
            );
          });
      });
    }

    // AS: Once the action has been dispatched for updating the bill account preference keep looking
    // at the store to make sure it has been completed, once it has been completed then call for the next
    // action of going paperless for the policy documents.
    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccountsNotifications),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessLoading),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessError),
      this.store.select(EsignatureSelectors.selectESignatureRequired),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessState),
      (
        billAccounts: BillingNotificationsState[],
        billAccountsAnyLoading,
        goPaperlessLoading,
        goPaperlessError,
        isESignatureRequired,
        policyPreference,
        policyPaperLessState
      ) => {
        return {
          billAccounts: billAccounts,
          loading:
            billAccountsAnyLoading ||
            goPaperlessLoading ||
            policyPaperLessState.goPaperLessState.loading,
          goPaperlessError: goPaperlessError,
          isESignatureRequired,
          policyPreference
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => !state.loading && !!correlationId),
        map(state => {
          const filtered = state.billAccounts.filter((ba: BillingNotificationsState) => {
            return ba.updatePreferenceCorrelationId === correlationId;
          });
          return Object.assign({}, state, { billAccounts: filtered });
        }),
        filter(state => state.billAccounts.length === _get(this.newBillingPreferences, 'length', 0))
      )
      .subscribe(state => {
        this.spinner.stop();
        const billAccounts: BillingNotificationsState[] = state.billAccounts;
        const policyPreferenceError: boolean = state.goPaperlessError;
        const billingPreferencesError: boolean = _some(billAccounts, {
          updateDeliveryPreferencesError: true
        });

        // Error.
        if (policyPreferenceError || billingPreferencesError) {
          this.navigateToOverview(false, true);
        } else {
          const policyPreference = state.policyPreference
            ? DeliveryPreferences.EMAIL
            : DeliveryPreferences.PAPER;
          // Success
          // Route to docusign if e-signature is required and current preference is paper
          if (state.isESignatureRequired && policyPreference === DeliveryPreferences.PAPER) {
            this.policyUtilService.dispatchGoPaperlessForGA('program/gopaperless');
            this.spinner.start({
              blockActions: true
            });
          } else {
            this.navigateToOverview(false, false);
          }
        }
      });
  }

  navigateToOverview(navigationRequestedFromButtonClick?: boolean, hasError?: boolean) {
    if (this.dispatched) {
      return true;
    }

    if (navigationRequestedFromButtonClick) {
      this.dispatchAnalyticsAction(
        null,
        PolicyAnalytics.buttonGoPaperlessNewCustomerExistingPreferenceClick
      );
    } else if (hasError) {
      this.dispatchAnalyticsAction(null, PolicyAnalytics.buttonGoPaperlessNewCustomerErrorClick);
    } else {
      this.dispatchAnalyticsAction(PolicyAnalytics.pageGoPaperlessNewCustomerConfirmation, null);
    }

    this.dispatched = true;

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  private enrollAfterDocuSign() {
    this.store.dispatch(GoPaperLessActions.EnrollGoPaperless());
    combineLatest(
      this.store.select(GoPaperLessSelectors.selectGoPaperlessLoading),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessError),
      (loading, hasError) => {
        return {
          loading: loading,
          hasError: hasError
        };
      }
    )
      .pipe(
        tap(() => {
          this.spinner.start({
            blockActions: true
          });
        }),
        filter(state => !state.loading),
        take(1)
      )
      .subscribe(state => {
        this.spinner.stop();
        this.navigateToOverview(false, state.hasError);
      });
  }

  private dispatchAnalyticsAction(
    pageAnalytic?: PageAnalyticParams,
    buttonAnalytic?: ButtonAnalyticParams
  ) {
    if (pageAnalytic) {
      this.store.dispatch(
        GoPaperLessActions.SendGoPaperlessAnalytics({
          pageAnalytic: pageAnalytic
        })
      );
    } else if (buttonAnalytic) {
      this.store.dispatch(
        GoPaperLessActions.SendGoPaperlessAnalytics({
          buttonAnalytic: buttonAnalytic
        })
      );
    }
  }
}
